import { SkillItem, StatItem, PortfolioItem, ServiceItem, SocialLink, PersonalInfo, PricingPlan, ExperienceItem, CertificationItem } from '../types';

export const PERSONAL_INFO: PersonalInfo = {
  name: 'Supratick Dey',
  email: 'me.suprodey@gmail.com',
  phone: '+91 8906428997',
  github: 'https://github.com/SupratickDey',
  linkedin: 'https://linkedin.com/in/supratick-dey',
  location: 'Kolkata, West Bengal, India',
};

export const SKILLS: SkillItem[] = [
  { name: 'GenAI · RAG Pipelines · LLM Fine-tuning', percentage: 92 },
  { name: 'BigQuery · Snowflake · Lakehouse', percentage: 93 },
  { name: 'Cloud Architecture (AWS · GCP · Azure)', percentage: 95 },
  { name: 'Python · Golang · Java', percentage: 92 },
  { name: 'Kubernetes · Docker · DevOps', percentage: 90 },
  { name: 'Apache Spark · Airflow · dbt · Dataform', percentage: 88 },
];

export const STATS: StatItem[] = [
  { number: 1000000, label: 'Active Users Served', format: 'compact', suffix: '+' },
  { number: 9, label: 'Asian Markets', format: 'number', suffix: '+' },
  { number: 7, label: 'Years Experience', format: 'number', suffix: '+' },
  { number: 40, label: 'Data Latency Reduction', format: 'number', suffix: '%' },
];

export const EXPERIENCE_ITEMS: ExperienceItem[] = [
  {
    period: 'Sep 2024 – Present',
    position: 'Solutions Architect',
    company: 'NextZen Minds',
    description: 'Leading end-to-end AI and data platform architecture for healthcare and fintech clients across Asia.',
    highlights: [
      'Architected RAG pipelines with LangChain & vector databases; fine-tuned domain-specific LLMs on Kubernetes (EKS/GKE)',
      'Built serverless AI document processing on AWS Lambda using Vision Transformers - 98% extraction accuracy, 15 min → 30 sec',
      'Migrated monolithic ETL to Lakehouse on BigQuery + Snowflake with Dataform and dbt - 40% data latency reduction',
      'Orchestrated complex workflows with Apache Airflow and Apache Spark for pharma/fintech clients',
      'Led 10+ engineers; delivered 25–30% monthly cloud cost reductions via FinOps audits',
    ]
  },
  {
    period: 'Jan 2022 – Sep 2024',
    position: 'Senior Programme Analyst',
    company: 'CBNITS',
    description: 'Led architectural transformation of a major pharma client\'s legacy monolith to cloud-native microservices across 9+ Asian markets.',
    highlights: [
      'Transformed legacy monolith to Golang microservices serving 1M+ users across 9+ Asian markets',
      'Built high-throughput GraphQL & REST APIs in Golang with sub-millisecond latency on Kubernetes',
      'Reduced deployment complexity by 70% via Kubernetes + Helm Charts with one-click rollouts',
      'Established observability stack (Datadog, Prometheus, Grafana) - 99.99% uptime maintained',
      'Awards: Team of the Quarter · Employee of the Month · Client Choice Award',
    ]
  },
  {
    period: 'May 2020 – Jan 2022',
    position: 'Programme Analyst',
    company: 'CBNITS',
    description: 'Built backend services and ETL pipelines for a regional B2B healthcare marketplace.',
    highlights: [
      'Developed GraphQL & RESTful APIs in Golang and Java/Spring Boot for B2B healthcare marketplace',
      'Designed ETL pipelines syncing large-scale datasets from SAP HANA to PostgreSQL',
      'Integrated Stripe, PayMaya, Google Maps, LDAP, and social auth; containerised with Docker Compose',
    ]
  },
  {
    period: 'Jun 2019 – May 2020',
    position: 'Software Engineer Trainee',
    company: 'CBNITS',
    description: 'Foundational backend engineering with Java Spring Boot and Node.js.',
    highlights: [
      'Built RESTful APIs using Java Spring Boot and Node.js with PostgreSQL on Linux',
      'Containerised backend services with Docker; established Agile/Git workflows',
    ]
  },
];

export const PORTFOLIO_ITEMS: PortfolioItem[] = [
  {
    id: 1,
    title: 'RAG & GenAI Document Platform',
    category: 'backend',
    image: '/images/project/project1.png',
    description: 'Serverless AI document processing pipeline on AWS Lambda using Vision Transformers and LangChain-powered RAG. Achieved 98% extraction accuracy on unstructured pharma documents, cutting processing time from 15 min to under 30 sec at scale.',
    technologies: ['AWS Lambda', 'Vision Transformers', 'LangChain', 'RAG', 'Pinecone', 'Kubernetes', 'FastAPI', 'GenAI'],
    github: '#',
    metric: '30x',
    metricLabel: 'Faster Processing',
  },
  {
    id: 2,
    title: 'Lakehouse Data Platform',
    category: 'backend',
    image: '/images/project/project2.png',
    description: 'Petabyte-scale Lakehouse on BigQuery and Snowflake using Dataform and dbt for transformation, Apache Airflow for orchestration, and Spark for large-scale batch jobs. Delivered 40% data latency reduction and unblocked self-service analytics.',
    technologies: ['BigQuery', 'Snowflake', 'Dataform', 'dbt', 'Apache Airflow', 'Apache Spark', 'Looker'],
    github: '#',
    metric: '40%',
    metricLabel: 'Latency Reduction',
  },
  {
    id: 3,
    title: 'eZHealth - Telehealth Platform',
    category: 'fullstack',
    image: '/images/project/project3.png',
    description: 'COVID vaccination and telehealth platform serving 9 Asian markets. Built on Golang microservices with GraphQL APIs, sub-millisecond latency, automated slot booking, and 99.99% uptime for 1M+ active users across Kubernetes clusters.',
    technologies: ['Golang', 'GraphQL', 'Kubernetes', 'PostgreSQL', 'ELK Stack', 'Helm', 'Stripe'],
    github: '#',
    metric: '1M+',
    metricLabel: 'Active Users',
  },
  {
    id: 4,
    title: 'Wankey - Biometric Identity Platform',
    category: 'fullstack',
    image: '/images/project/project4.png',
    description: 'Air-gapped biometric identity platform with zero-data-leakage architecture managing 1M+ identities. Integrated DeepFace recognition and OCR-based document verification achieving 99% accuracy across multi-tenant Kubernetes deployments.',
    technologies: ['Python', 'DeepFace', 'OCR', 'Microservices', 'Kubernetes', 'OAuth 2.0', 'Redis'],
    github: '#',
    metric: '99%',
    metricLabel: 'Verification Accuracy',
  },
  {
    id: 5,
    title: 'eZRx - B2B Medicine Marketplace',
    category: 'backend',
    image: '/images/project/project1.png',
    description: 'Regional B2B pharmaceutical marketplace with SAP HANA to PostgreSQL ETL pipelines, LDAP enterprise auth, and multi-gateway payment integration (Stripe, PayMaya). Deployed across Southeast Asia for leading pharma distributor.',
    technologies: ['Golang', 'Java', 'SAP HANA', 'PostgreSQL', 'GraphQL', 'Docker', 'Spring Boot'],
    github: '#',
    metric: '9+',
    metricLabel: 'Markets Deployed',
  },
  {
    id: 6,
    title: 'Cloud FinOps & Infra Automation',
    category: 'backend',
    image: '/images/project/project2.png',
    description: 'Multi-cloud IaC platform using Terraform and CloudFormation spanning AWS, GCP, and Azure. Implemented FinOps dashboards, auto-scaling policies, and spot instance strategies - delivering 25-30% monthly cost reductions with 99.99% SLA.',
    technologies: ['Terraform', 'CloudFormation', 'AWS', 'GCP', 'Azure', 'Kubernetes', 'Datadog', 'Grafana'],
    github: '#',
    metric: '28%',
    metricLabel: 'Avg Cost Reduction',
  }
];

export const SERVICES: ServiceItem[] = [
  {
    icon: '/images/icon/service1.svg',
    title: 'GenAI & RAG Architecture',
    description: 'Production-grade RAG pipelines with LangChain and vector databases (Pinecone, Weaviate). Fine-tuning domain-specific LLMs and deploying scalable inference endpoints on Kubernetes - validated at 98% extraction accuracy in healthcare.',
    tools: ['LangChain', 'Pinecone', 'LLM Fine-tuning', 'RAG', 'FastAPI', 'Kubernetes'],
  },
  {
    icon: '/images/icon/service2.svg',
    title: 'Lakehouse & Data Platform Engineering',
    description: 'Petabyte-scale Lakehouse design on BigQuery and Snowflake. End-to-end transformation pipelines in Dataform and dbt, workflow orchestration with Apache Airflow, and distributed processing with Spark - delivering 40% latency reduction.',
    tools: ['BigQuery', 'Snowflake', 'dbt', 'Dataform', 'Apache Airflow', 'Spark'],
  },
  {
    icon: '/images/icon/service3.svg',
    title: 'Cloud Architecture & FinOps',
    description: 'Multi-cloud, cloud-native architectures on AWS, GCP, and Azure. IaC with Terraform and CloudFormation, serverless design, auto-scaling strategies, and FinOps audits - achieving 25-30% monthly cost reductions consistently.',
    tools: ['Terraform', 'AWS', 'GCP', 'Azure', 'CloudFormation', 'Kubernetes'],
  },
  {
    icon: '/images/icon/service1.svg',
    title: 'AI/ML Integration & Vision Systems',
    description: 'Vision Transformers, OCR pipelines, and ML model integration for document intelligence and identity verification. Productionised on serverless AWS Lambda with 30x throughput improvements and sub-second inference latency.',
    tools: ['Vision Transformers', 'DeepFace', 'OCR', 'AWS Lambda', 'Python', 'TensorFlow'],
  },
  {
    icon: '/images/icon/service2.svg',
    title: 'High-Performance Backend Engineering',
    description: 'High-throughput GraphQL and REST APIs in Golang, Java, and Python. Sub-millisecond response times, multi-region Kubernetes deployments, and observability stacks (Datadog, Prometheus, Grafana) maintaining 99.99% uptime at 1M+ user scale.',
    tools: ['Golang', 'GraphQL', 'PostgreSQL', 'Redis', 'Datadog', 'Helm'],
  },
  {
    icon: '/images/icon/service3.svg',
    title: 'Technology Consultation & Pre-Sales',
    description: 'Translating complex enterprise requirements into scalable solution architectures for pre-sales engagements. Delivery governance with Agile/Scrum, CI/CD pipelines, and regulatory compliance frameworks (GDPR · HIPAA · HL7).',
    tools: ['Architecture Design', 'Agile', 'CI/CD', 'HIPAA', 'GDPR', 'HL7'],
  },
];

export const CERTIFICATIONS: CertificationItem[] = [
  {
    title: 'Apache Spark Essential Training: Big Data Engineering',
    issuer: 'LinkedIn Learning',
    icon: 'spark',
  },
  {
    title: 'Advanced AI: Transformers for Computer Vision',
    issuer: 'LinkedIn Learning',
    icon: 'ai',
  },
  {
    title: 'Getting Started with AWS Cloud Essentials',
    issuer: 'AWS Training & Certification',
    icon: 'cloud',
  },
  {
    title: 'CutShort Certified Go Programming (Golang)',
    issuer: 'CutShort',
    icon: 'code',
  },
  {
    title: 'Problem Solving Through Programming in C',
    issuer: 'NPTEL',
    icon: 'award',
  },
];

export const SOCIAL_LINKS: SocialLink[] = [
  {
    platform: 'email',
    url: `mailto:${PERSONAL_INFO.email}`,
    icon: 'email',
    label: 'Email'
  },
  {
    platform: 'github',
    url: PERSONAL_INFO.github,
    icon: 'github',
    label: 'GitHub'
  },
  {
    platform: 'linkedin',
    url: PERSONAL_INFO.linkedin,
    icon: 'linkedin',
    label: 'LinkedIn'
  },
  {
    platform: 'twitter',
    url: 'https://twitter.com/SupratickDey',
    icon: 'twitter',
    label: 'Twitter'
  },
];

export const TECHNOLOGIES = [
  'LangChain', 'RAG Pipelines', 'LLM Fine-tuning', 'BigQuery',
  'Snowflake', 'Dataform', 'dbt', 'Apache Spark',
  'AWS', 'GCP', 'Azure', 'Python',
  'Golang', 'Java', 'Kubernetes', 'Docker',
  'Apache Airflow', 'Grafana', 'Datadog', 'Terraform',
  'FastAPI', 'PostgreSQL', 'Vector Databases', 'Helm',
];

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'consultation',
    name: 'CONSULTATION',
    price: 150,
    description: 'Perfect for initial architecture assessment and planning',
    features: [
      'Architecture Review',
      'Technology Recommendations',
      'Cloud Migration Strategy',
      'Performance Analysis',
      'Security Assessment',
      '2-hour session'
    ],
    buttonText: 'Book Consultation'
  },
  {
    id: 'project',
    name: 'PROJECT SOLUTION',
    price: 5000,
    description: 'Complete solution development and implementation',
    features: [
      'Full Solution Architecture',
      'Cloud Infrastructure Setup',
      'AI/ML Model Integration',
      'Data Pipeline Development',
      'DevOps & Monitoring',
      'Team Training',
      '3-month support'
    ],
    featured: true,
    buttonText: 'Start Project'
  },
  {
    id: 'enterprise',
    name: 'ENTERPRISE',
    price: 0,
    description: 'Custom enterprise solutions for large-scale transformations',
    features: [
      'Enterprise Architecture Design',
      'Multi-cloud Strategy',
      'Legacy System Migration',
      'GenAI & Lakehouse Platform',
      'Team Leadership & Training',
      'Ongoing Technical Advisory',
      'Custom SLA & Support'
    ],
    buttonText: "Let's Discuss"
  }
];

export const NAVIGATION_ITEMS = [
  { id: 'about',          label: 'About' },
  { id: 'skills',         label: 'Experience' },
  { id: 'portfolio',      label: 'Work' },
  { id: 'services',       label: 'Services' },
  { id: 'certifications', label: 'Credentials' },
];
